import { useCallback, useEffect, useState, useSyncExternalStore } from "react";

/**
 * How a long list is read: keep scrolling, or move a page at a time.
 *
 * Both are the same query. `useWindowedRows` fetches one page whenever it is asked to, and the
 * only difference between the two modes is what the table renders out of what has arrived and
 * what asks for the next page — a sentinel near the bottom, or a Next button.
 *
 * The preference is one setting for the whole admin portal rather than per table. It came in as
 * "this should be on every paginated thing", and a reader who wants pages wants them everywhere;
 * a per-table memory would mean setting it again on each screen and finding tables that disagree.
 */
export type ListMode = "scroll" | "pages";

const STORAGE_KEY = "athena.admin.listMode";

/** Infinite scroll, because that is what every one of these tables did before the choice existed. */
const DEFAULT_MODE: ListMode = "scroll";

const isListMode = (value: unknown): value is ListMode => value === "scroll" || value === "pages";

/*
 * A module-level store rather than context, read through `useSyncExternalStore`.
 *
 * Tables sit on different routes and inside different cards, so there is no one place to hang a
 * provider that covers them all without wrapping the entire app for a two-value setting. The store
 * is what makes the choice take effect on every mounted table at once instead of on the next
 * remount of each.
 */
const listeners = new Set<() => void>();
let cached: ListMode | undefined;

const snapshot = (): ListMode => {
    if (cached) return cached;
    try {
        const stored = window.localStorage.getItem(STORAGE_KEY);
        cached = isListMode(stored) ? stored : DEFAULT_MODE;
    } catch {
        // Private browsing, or storage disabled. The choice just does not outlive the session.
        cached = DEFAULT_MODE;
    }
    return cached;
};

const subscribe = (onChange: () => void): (() => void) => {
    listeners.add(onChange);
    return () => {
        listeners.delete(onChange);
    };
};

export const setListMode = (mode: ListMode): void => {
    cached = mode;
    try {
        window.localStorage.setItem(STORAGE_KEY, mode);
    } catch {
        // See `snapshot` — remembering it is a convenience, not a requirement.
    }
    for (const onChange of listeners) onChange();
};

/** The current mode, re-rendering every table that reads it when somebody changes it. */
export const useListMode = (): ListMode => useSyncExternalStore(subscribe, snapshot, () => DEFAULT_MODE);

export interface ListView<TRow> {
    mode: ListMode;
    /** The rows to render: everything loaded in scroll mode, one page's worth in pages mode. */
    rows: TRow[];
    /** Zero-based, so `page + 1` is what a person is shown. */
    page: number;
    hasPrev: boolean;
    hasNext: boolean;
    toPrev: () => void;
    toNext: () => void;
    /** The next page is being fetched and there is nothing yet to show for it. */
    awaitingPage: boolean;
    /**
     * Whether paging this list would do anything.
     *
     * False for a list that fits on one page with nothing more to fetch — most admin tables, most
     * of the time. The control hides itself on those rather than offering a choice between one
     * page and the same one page.
     */
    canPage: boolean;
    /** 1-based index of the first rendered row, 0 when there are none — for an "x–y of z" label. */
    from: number;
    /** 1-based index of the last rendered row, 0 when there are none. */
    to: number;
}

interface ListViewOptions {
    /**
     * Whether the server has rows this list has not fetched.
     *
     * Optional because most admin tables read their whole list in one query — for those, what is
     * loaded is everything, and paging is a slice of it.
     */
    hasMore?: boolean;
    /** Asks for the next page. Unused, and unnecessary, when `hasMore` is false. */
    loadMore?: () => void;
    pageSize?: number;
}

/** Rows per page for a table that already holds its whole list. */
export const LIST_PAGE_SIZE = 50;

const NO_MORE = () => undefined;

/**
 * Turns the rows `useWindowedRows` has accumulated into whichever view the reader chose.
 *
 * ponytail: pages are sliced out of the rows already fetched, so paging deep holds every earlier
 * page in memory exactly as scrolling that far would. That is the same ceiling infinite scroll
 * already has and nobody has reached it; a genuinely windowed read means teaching the Apollo field
 * policy to return a slice, which is worth doing only if someone starts paging into the thousands.
 */
export const useListView = <TRow>(
    loaded: TRow[],
    { hasMore = false, loadMore = NO_MORE, pageSize = LIST_PAGE_SIZE }: ListViewOptions = {},
): ListView<TRow> => {
    const mode = useListMode();
    const [
        page,
        setPage,
    ] = useState(0);

    /*
     * A page that no longer exists sends the reader back to one that does.
     *
     * Filtering and searching replace the row set underneath an open page — search on page 6 of
     * users and the result is three rows — and without this the table would render empty rather
     * than the three rows the person just asked for.
     */
    useEffect(() => {
        if (page > 0 && !hasMore && page * pageSize >= loaded.length) {
            setPage(Math.max(0, Math.ceil(loaded.length / pageSize) - 1));
        }
    }, [
        loaded.length,
        hasMore,
        page,
        pageSize,
    ]);

    /*
     * Fetch what the open page needs.
     *
     * In scroll mode the sentinel does this. In pages mode nothing else would: the rows for page 3
     * have only been fetched if somebody walked there one page at a time, and a clamp or a mode
     * switch can land on a page whose rows have not arrived.
     */
    const wantsThrough = (page + 1) * pageSize;
    useEffect(() => {
        if (mode === "pages" && hasMore && loaded.length < wantsThrough) loadMore();
    }, [
        mode,
        hasMore,
        loaded.length,
        wantsThrough,
        loadMore,
    ]);

    const toPrev = useCallback(() => setPage((p) => Math.max(0, p - 1)), []);
    const toNext = useCallback(() => setPage((p) => p + 1), []);

    const canPage = loaded.length > pageSize || hasMore;

    if (mode === "scroll") {
        return {
            mode,
            rows: loaded,
            page: 0,
            hasPrev: false,
            hasNext: false,
            toPrev,
            toNext,
            awaitingPage: false,
            canPage,
            from: loaded.length > 0 ? 1 : 0,
            to: loaded.length,
        };
    }

    const rows = loaded.slice(page * pageSize, wantsThrough);
    return {
        mode,
        rows,
        page,
        hasPrev: page > 0,
        // A full page with more behind it, or more still to fetch — either way there is a next.
        hasNext: loaded.length > wantsThrough || hasMore,
        toPrev,
        toNext,
        awaitingPage: rows.length === 0 && hasMore,
        canPage,
        from: rows.length > 0 ? page * pageSize + 1 : 0,
        to: page * pageSize + rows.length,
    };
};
